import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  useMediaQuery,
  Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { changeOrderStatus } from "../../../../../services/OrderStatus/OrderStatusApi";
import { ThemeContext } from "../../../../../context/ThemeContextProvider";
import { fetchDeliveryOrders } from "../../../../../services/pos/Tables";
import CreateOrderForm from "../../MakeOrderResponsive/MobileMakeOrder/deliveryMobile/OrderDetails/Openorder/CreateOrderForm";
import DeliveryModalContent from "../../MakeOrderResponsive/MobileMakeOrder/deliveryMobile/DeliveryModalContent";
import { usePermissions } from "../../../../../context/PermissionsAndFeatures/UserPermissionsContext";
import OrderItemsDialog from "./OrderItemsDialog";

const DeliverySection = () => {
  const { mode } = useContext(ThemeContext);
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState("الكل");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isAddItemsOpen, setIsAddItemsOpen] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [pilotFilter, setPilotFilter] = useState("");
  const { hasPermission } = usePermissions();

  // NEW: حالة Dialog عرض أصناف الأوردر
  const [orderItemsDialogOpen, setOrderItemsDialogOpen] = useState(false);
  const [selectedOrderIdForItems, setSelectedOrderIdForItems] = useState(null);

  // هل يمكن للمستخدم رؤية طلبات الحالة "طلب خارجي"؟
  const canViewExternalOrders = hasPermission("orders_delivery_api_orders");

  // الحالات المتاحة للتغيير
  const statusOptions = [
    { value: "out_of_preparation", label: "مكتمل" },
    { value: "pending", label: "تجهيز" },
  ];

  // تغيير حالة الأوردر
  const handleChangeOrderStatus = async () => {
    if (!editOrderId || !selectedStatus) return;
    try {
      await changeOrderStatus(editOrderId, selectedStatus);
      setEditStatusOpen(false);
      setEditOrderId(null);
      setSelectedStatus("");
      loadOrders();
    } catch (err) {
      alert("فشل تغيير حالة الأوردر");
    }
  };

  const loadOrders = async () => {
    try {
      const branchId = localStorage.getItem("branch_id");
      if (!branchId) throw new Error("❌ لا يوجد branch_id في localStorage");

      const data = await fetchDeliveryOrders(branchId);
      const enriched = data.map((item) => ({
        id: item.id,
        status:
          item.status === "pending"
            ? "في التجهيز"
            : item.status === "completed"
            ? "مكتمل"
            : item.status === "out_of_preparation"
            ? "مكتمل"
            : item.status === "underway"
            ? "قيد التوصيل"
            : item.status === "delivered"
            ? "تم التسليم"
            : item.status === "under_review"
            ? "طلب خارجي"
            : item.status,
        customer: item.customer?.name || "-",
        pilotName: item.delivery?.name || "-",
        phone: item.customer?.phone1 || "",
        address: item.customer?.address1 || "",
        time: item.open_time || "-",
        total: item.total || 0,
        orderNumber: `#0${item.general_increment}`,
        timer: 0,
        // NEW: عدد الأصناف في الطلب
        itemsCount: Array.isArray(item.items) ? item.items.length : 0,
      }));

      // إذا المستخدم لا يملك صلاحية الاطلاع على طلبات "طلب خارجي" نزيلها من القائمة
      const finalList = canViewExternalOrders
        ? enriched
        : enriched.filter((o) => o.status !== "طلب خارجي");

      setOrders(finalList);
    } catch (err) {
      console.error("❌ فشل تحميل طلبات الدليفري:", err.message);
    }
  };

  // إعادة تحميل عند تغيير صلاحية العرض أيضاً
  useEffect(() => {
    loadOrders();
  }, [canViewExternalOrders]);

  // تم إلغاء عداد الوقت
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setOrders((prevOrders) =>
  //       prevOrders.map((order) =>
  //         ["في التجهيز", "قيد التوصيل"].includes(order.status)
  //           ? { ...order, timer: order.timer + 1 }
  //           : order
  //       )
  //     );
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, []);
  useEffect(() => {
    localStorage.setItem("current_component", "delivery");
  }, []);

  // ✅ useEffect لإضافة اختصار F6 لإنشاء طلب جديد
  useEffect(() => {
    const handleKeyDown = (e) => {
      // التحقق من F6 (keyCode 117)
      if (e.key === 'F6' || e.keyCode === 117) {
        e.preventDefault(); // منع السلوك الافتراضي
        if (hasPermission("orders_other_edite_service")) {
          setIsCreateOpen(true);
        }
      }
    };

    // إضافة المستمع
    window.addEventListener('keydown', handleKeyDown);

    // تنظيف المستمع عند unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasPermission]);

  // تحديث البيانات كل 10 دقائق
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     loadOrders();
  //   }, 10 * 60 * 1000); // 10 دقائق بالمللي ثانية
  //   return () => clearInterval(interval);
  // }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const formatTime12 = (timeStr) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":");
    let hour = Number(h);
    const ampm = hour >= 12 ? "م" : "ص";
    hour = hour % 12 || 12;
    return `${hour}:${m} ${ampm}`;
  };

  // حساب عدد الطلبات لكل حالة
  const tabStatuses = [
    { key: "الكل", label: "الكل" },
    { key: "مكتمل", label: "مكتمل" },
    { key: "في التجهيز", label: "في التجهيز" },
    { key: "قيد التوصيل", label: "قيد التوصيل" },
    { key: "تم التسليم", label: "تم التسليم" },
    { key: "طلب خارجي", label: "طلب خارجي" }, // 👈 تابة جديدة
  ];

  // لو المستخدم لا يملك الصلاحية نخفي تاب "طلب خارجي"
  const visibleTabStatuses = tabStatuses.filter(
    (t) => !(t.key === "طلب خارجي" && !canViewExternalOrders)
  );

  const getTabCount = (status) => {
    if (status === "الكل") {
      // عدد الطلبات المعروضة فعلياً في تاب الكل
      return orders.filter((order) => {
        let statusAr = order.status;
        if (order.status === "underway") statusAr = "قيد التوصيل";
        if (order.status === "delivered") statusAr = "تم التسليم";
        if (order.status === "under_review") statusAr = "طلب خارجي";
        // استبعاد "طلب خارجي" و "finished"
        return !(statusAr === "طلب خارجي" || order.status === "finished");
      }).length;
    }
    return orders.filter((order) => {
      let statusAr = order.status;
      if (order.status === "underway") statusAr = "قيد التوصيل";
      if (order.status === "delivered") statusAr = "تم التسليم";
      if (order.status === "under_review") statusAr = "طلب خارجي";
      return statusAr === status;
    }).length;
  };

  const filteredOrders = orders.filter((order) => {
    // تحويل حالة النظام إلى العربية للفلترة
    let statusAr = order.status;
    if (order.status === "underway") statusAr = "قيد التوصيل";
    if (order.status === "delivered") statusAr = "تم التسليم";
    if (order.status === "under_review") statusAr = "طلب خارجي";

    // في تاب "الكل" لا تعرض "طلب خارجي" ولا "finished"
    if (
      filterStatus === "الكل" &&
      (statusAr === "طلب خارجي" || order.status === "finished")
    )
      return false;

    const searchFields = [
      order.customer,
      order.pilotName,
      order.phone,
      order.address,
      order.time,
      order.total.toString(),
      order.orderNumber,
    ]
      .join(" ")
      .toLowerCase();

    return (
      (filterStatus === "الكل" || statusAr === filterStatus) &&
      searchFields.includes(searchQuery.toLowerCase()) &&
      (pilotFilter === "" || order.pilotName === pilotFilter)
    );
  });

  const statusColors = {
    مكتمل: "#4CAF50",
    "في التجهيز": "#FFC107",
    "قيد التوصيل": "#2196F3",
  };

  // NEW: دالة فتح Dialog عرض الأصناف
  const handleOpenOrderItems = (orderId) => {
    setSelectedOrderIdForItems(orderId);
    setOrderItemsDialogOpen(true);
  };
  
  // NEW: دالة إغلاق Dialog عرض الأصناف
  const handleCloseOrderItems = () => {
    setOrderItemsDialogOpen(false);
    setSelectedOrderIdForItems(null);
  };

  // detect mobile screens
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ p: isMobile ? 1 : 2, bgcolor: mode === "dark" ? "#1F1D2B" : "#fff", pb: isMobile ? "160px" : "100px" }}>
      {/* فلاتر البحث */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "stretch",
          mb: isMobile ? 1 : 2,
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", width: "100%" }}>
          {visibleTabStatuses.map((tab) => (
            <Button
              key={tab.key}
              variant={filterStatus === tab.key ? "contained" : "outlined"}
              onClick={() => setFilterStatus(tab.key)}
              size="small"
              sx={{ px: isMobile ? 2 : 1.5, py: isMobile ? 0.8 : 0.6, fontSize: isMobile ? 14 : 13 }}
            >
              {tab.label}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 1.2,
                  py: 0.2,
                  bgcolor: "#fff",
                  color: "#232334",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  fontSize: "13px",
                  minWidth: "28px",
                  display: "inline-block",
                }}
              >
                {getTabCount(tab.key)}
              </Box>
            </Button>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexDirection: "column", width: "100%" }}>
          <TextField
            placeholder="البحث عن الطلبات..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchQuery("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="الدليفري"
            size="small"
            value={pilotFilter || ""}
            onChange={(e) => setPilotFilter(e.target.value)}
            fullWidth
          >
            <MenuItem value="">الكل</MenuItem>
            {[...new Set(orders.map((o) => o.pilotName).filter(Boolean))].map(
              (name, idx) => (
                <MenuItem key={idx} value={name}>
                  {name}
                </MenuItem>
              )
            )}
          </TextField>
          {hasPermission("orders_other_edite_service") && (
            <Button variant="contained" onClick={() => setIsCreateOpen(true)} fullWidth sx={{ py: 1 }}>
              إنشاء طلب جديد
            </Button>
          )}
        </Box>
      </Box>
      {/* قائمة الطلبات */}
      <Grid container spacing={2}>
        {filteredOrders.map((order, idx) => (
          <Grid item xs={12} key={order.id}>
            <Paper
              sx={{
                background: "linear-gradient(135deg, #232334 60%, #232334 100%)",
                color: "#fff",
                borderRadius: 2,
                boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                p: isMobile ? 1 : 0,
                overflow: "hidden",
                minHeight: isMobile ? 110 : 170,
                position: "relative",
                cursor: "pointer",
                transition: "transform 0.12s",
                "&:active": { transform: "scale(0.997)" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
              onClick={() => {
                setSelectedOrderId(order.id);
                setIsAddItemsOpen(true);
              }}
            >
               {/* Header: Order Number & Status */}
               <Box
                 sx={{
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "space-between",
                   p: 1.2,
                   pb: 0.5,
                   gap: 2,
                   position: "relative",
                 }}
               >
                 {/* NEW: أيقونة عدد الأصناف - في البداية */}
                 {order.itemsCount > 0 && (
                   <Box
                     sx={{
                       zIndex: 10,
                       cursor: "pointer",
                     }}
                     onClick={(e) => {
                       e.stopPropagation();
                       handleOpenOrderItems(order.id);
                     }}
                   >
                     <Badge
                       badgeContent={order.itemsCount}
                       color="error"
                       max={99}
                       sx={{
                         "& .MuiBadge-badge": {
                           fontSize: "8px",
                           fontWeight: 700,
                           minWidth: "14px",
                           height: "14px",
                           padding: "0 3px",
                         },
                       }}
                     >
                       <Box
                         sx={{
                           bgcolor: mode === "dark" ? "#1a1a2e" : "#fff",
                           borderRadius: "50%",
                           width: 28,
                           height: 28,
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center",
                           boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                           border: `2px solid ${mode === "dark" ? "#444" : "#e0e0e0"}`,
                           transition: "all 0.2s",
                           "&:active": {
                             transform: "scale(0.95)",
                           },
                         }}
                       >
                         <RestaurantMenuIcon
                           sx={{
                             fontSize: 14,
                             color: mode === "dark" ? "#fff" : "#666",
                           }}
                         />
                       </Box>
                     </Badge>
                   </Box>
                 )}

                 {/* المحتوى الموجود - الحالة ورقم الطلب */}
                 <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, justifyContent: "flex-end" }}>
                   {/* الحالة بشكل شارة دائرية مع نص */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>{order.status}</Typography>
                    {hasPermission("orders_other_edite_service") && (
                      <IconButton
                        size="small"
                        sx={{ ml: 0.5, color: "#FFC107" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditOrderId(order.id);
                          setEditStatusOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor:
                          order.status === "مكتمل"
                            ? "#4CAF50"
                            : order.status === "في التجهيز"
                            ? "#FFC107"
                            : order.status === "قيد التوصيل"
                            ? "#2196F3"
                            : "#fff",
                      }}
                    />
                  </Box>
                   {/* رقم الطلب */}
                   <Box
                     sx={{
                       bgcolor: "#1ecb6b",
                       color: "#fff",
                       px: 2,
                       py: 0.5,
                       borderRadius: "12px",
                       fontWeight: 700,
                       fontSize: 18,
                       boxShadow: "0 2px 8px #0002",
                       display: "flex",
                       alignItems: "center",
                       minWidth: 60,
                       justifyContent: "center",
                     }}
                   >
                     {order.orderNumber}
                   </Box>
                 </Box>
               </Box>

               {/* Time */}
               <Box
                 sx={{
                   display: "flex",
                   alignItems: "center",
                   gap: 1,
                   px: 2,
                   pt: 0.5,
                 }}
               >
                 <Typography variant="caption" sx={{ color: "#b0bec5" }}>
                   {formatTime12(order.time)}
                 </Typography>
                 {/* تم إلغاء التايمر */}
               </Box>

               {/* Main Content */}
               <Box
                 sx={{
                   px: 2,
                   py: 1.5,
                   display: "flex",
                   flexDirection: "column",
                   gap: 0.5,
                   alignItems: "flex-end",
                 }}
               >
                 <Typography
                   sx={{ fontWeight: 700, fontSize: 18, color: "#fff" }}
                 >
                   {order.customer}
                 </Typography>
                 <Typography sx={{ fontSize: 15, color: "#fff" }}>
                   {order.phone}
                 </Typography>
                 <Typography sx={{ fontSize: 15, color: "#b0bec5" }}>
                   {order.address}
                 </Typography>
                 {/* عرض اسم الدليفري */}
                 <Typography
                   sx={{ fontSize: 15, color: "#90caf9", fontWeight: 500 }}
                 >
                   الدليفري: {order.pilotName}
                 </Typography>
               </Box>

               {/* Footer: Total */}
               <Box
                 sx={{
                   bgcolor: "#F8BBD0",
                   color: "#fff",
                   fontWeight: 700,
                   fontSize: 20,
                   px: 2.5,
                   py: 0.7,
                   borderRadius: "16px",
                   position: "absolute",
                   bottom: 12,
                   right: 12,
                   boxShadow: "0 2px 8px #0002",
                   minWidth: 70,
                   textAlign: "center",
                   letterSpacing: 1.2,
                   border: "2px solid #fff2",
                 }}
               >
                ${order.total}
               </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
      {/* مودال إنشاء الطلب */}
      {isCreateOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.25)",
            zIndex: 1400,
            display: "flex",
            alignItems: isMobile ? "flex-end" : "center",
            justifyContent: "center",
            p: 0,
          }}
        >
          <Box
            sx={{
              bgcolor: mode === "dark" ? "#232334" : "#fff",
              borderTopLeftRadius: isMobile ? 16 : 6,
              borderTopRightRadius: isMobile ? 16 : 6,
              boxShadow: 8,
              p: 2,
              width: isMobile ? "100%" : "min(900px, 96vw)",
              maxHeight: "98vh",
              overflowY: "auto",
              position: "relative",
            }}
          >
            <CreateOrderForm
              onSuccess={(res) => {
                const createdId = res?.id;
                if (!createdId) return alert("❌ فشل في جلب رقم الطلب الجديد");

                setSelectedOrderId(createdId);
                setIsCreateOpen(false);
                setIsAddItemsOpen(true);
                loadOrders();
              }}
            />
            <IconButton
              onClick={() => setIsCreateOpen(false)}
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                color: "#EA7C69",
                zIndex: 10,
              }}
            >
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      {/* Dialog TableModalDialog */}
      <DeliveryModalContent
        open={isAddItemsOpen}
        onClose={() => setIsAddItemsOpen(false)}
        type="addItem"
        orderId={selectedOrderId}
        tableId={null}
        rootId={localStorage.getItem("branch_id")}
        onSuccess={() => {
          setIsAddItemsOpen(false);
          loadOrders();
        }}
      />
      {/* مودال تغيير الحالة */}
      {editStatusOpen && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            bottom: 0,
            width: "100vw",
            zIndex: 1500,
            display: "flex",
            justifyContent: "center",
            p: 0,
          }}
        >
          <Box
            sx={{
              bgcolor: mode === "dark" ? "#232334" : "#fff",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              boxShadow: 8,
              p: 2,
              width: "100%",
              maxWidth: isMobile ? "100vw" : 560,
              color: mode === "dark" ? "#fff" : "#232334",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: mode === "dark" ? "#fff" : "#232334" }}>
              تغيير حالة الطلب
            </Typography>
            {(() => {
              const order = orders.find((o) => o.id === editOrderId);
              if (!order) return null;
              return (
                <Box sx={{ mb: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={{ fontWeight: "bold", color: mode === "dark" ? "#fff" : "#232334" }}>العميل: {order.customer}</Typography>
                  <Typography sx={{ color: mode === "dark" ? "#fff" : "#232334" }}>الهاتف: {order.phone}</Typography>
                </Box>
              );
            })()}
            <TextField
              select
              label="اختر الحالة الجديدة"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            >
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button variant="contained" color="primary" onClick={handleChangeOrderStatus} disabled={!selectedStatus}>
                حفظ
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setEditStatusOpen(false);
                  setEditOrderId(null);
                  setSelectedStatus("");
                }}
              >
                إلغاء
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {/* NEW: Dialog عرض أصناف الأوردر */}
      <OrderItemsDialog
        open={orderItemsDialogOpen}
        onClose={handleCloseOrderItems}
        orderId={selectedOrderIdForItems}
      />
    </Box>
  );
};

export default DeliverySection;
